import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to dashboard if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(credential, password);
      
      // Store token in localStorage if remember me checked
      if (rememberMe) {
        const token = sessionStorage.getItem('token');
        if (token) {
          localStorage.setItem('token', token);
        }
      }
      
      // Redirect to return URL or dashboard
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      
      // Map specific error messages
      if (errorMessage.includes('429')) {
        setError('Too many failed login attempts. Please try again later.');
      } else if (errorMessage.includes('pending approval')) {
        setError('Your account is pending approval by an administrator. You will receive an email when approved.');
      } else if (errorMessage.includes('deactivated')) {
        setError('Your account has been deactivated. Please contact support.');
      } else if (errorMessage.includes('Invalid credentials')) {
        setError('Invalid email/username or password. Please try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 p-4">
      <div className="w-full max-w-md">
        <Card variant="elevated">
          <Card.Header
            title="Welcome Back"
            subtitle="Sign in to your account"
          />
          <Card.Body>
            {error && (
              <div className="mb-4 rounded-lg p-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                id="credential"
                type="text"
                label="Email or Username"
                required
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                disabled={loading}
                placeholder="Enter your email or username"
                autoComplete="username"
              />

              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="Enter your password"
                autoComplete="current-password"
                rightIcon={showPassword ? EyeOff : Eye}
                onRightIconClick={() => setShowPassword(!showPassword)}
                rightIconLabel={showPassword ? 'Hide password' : 'Show password'}
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Remember me
                  </label>
                </div>
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-primary dark:text-primary-400 hover:text-primary/80 dark:hover:text-primary-300 font-medium">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading || !credential || !password}
                className="w-full"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </Card.Body>
          <Card.Footer>
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary dark:text-primary-400 hover:text-primary/80 dark:hover:text-primary-300 font-medium">
                Sign up
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
